import React from 'react';
import { motion } from 'framer-motion';
import { useMemo, useState, useEffect } from 'react';
import { usePageVisibility } from '@/hooks/usePageVisibility';

interface FloatingParticlesProps {
  count?: number;
  variant: 'focus' | 'admin' | 'break';
}

export const FloatingParticles = React.memo(function FloatingParticles({ count = 6, variant }: FloatingParticlesProps) {
  const isVisible = usePageVisibility();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const orbs = useMemo(() => {
    return Array.from({ length: count }, (_, i) => {
      const size = Math.random() * 60 + 60; // 60-120px
      const startX = Math.random() * 100;
      const startY = Math.random() * 100;
      const endX = Math.random() * 100;
      const endY = Math.random() * 100;
      const duration = Math.random() * 30 + 50; // 50-80s - slower for less GPU work
      const delay = Math.random() * 10;
      return {
        id: i,
        size,
        startX,
        startY,
        endX,
        endY,
        duration,
        delay,
      };
    });
  }, [count]);

  const colorVar = variant === 'break' ? 'success' : variant === 'focus' ? 'primary' : 'secondary';

  if (prefersReducedMotion || !isVisible) return null;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {orbs.map((orb) => (
        <motion.div
          key={orb.id}
          className="absolute rounded-full"
          style={{
            width: orb.size,
            height: orb.size,
            left: `${orb.startX}%`,
            top: `${orb.startY}%`,
            background: `radial-gradient(circle at 30% 30%, hsl(var(--${colorVar}) / 0.25), hsl(var(--${colorVar}) / 0.05))`,
            filter: 'blur(1px)',
            willChange: 'transform',
          }}
          animate={{
            x: [`0%`, `${(orb.endX - orb.startX) * 2}%`, `${(orb.endX - orb.startX)}%`, `0%`],
            y: [`0%`, `${(orb.endY - orb.startY)}%`, `${(orb.endY - orb.startY) * 2}%`, `0%`],
          }}
          transition={{
            duration: orb.duration,
            repeat: Infinity,
            delay: orb.delay,
            ease: 'linear',
          }}
        />
      ))}
    </div>
  );
});
